import React from "react"
import styled from "styled-components"
import Img from "gatsby-image"
import { graphql } from "gatsby"
import { useIntl } from "gatsby-plugin-intl"
import {
  FiArrowRight,
  FiArrowDown,
  FiCode,
  FiCloud,
  FiShield,
  FiCpu,
  FiLayers,
  FiLink,
  FiCheck,
  FiBookOpen,
  FiFolder,
  FiTarget,
} from "react-icons/fi"
import Link from "../components/Link"
import PageMetadata from "../components/PageMetadata"
import { translateMessageId } from "../utils/translations"

const Page = styled.div`
  width: 100%;
  color: ${({ theme }) => theme.colors.text};
  h1,
  h2,
  h3 {
    font-family: inherit;
    font-style: normal;
  }
  h2 {
    font-size: clamp(1.9rem, 3vw, 2.75rem);
    font-weight: 500;
    line-height: 1.15;
    letter-spacing: -0.035em;
    margin: 0 0 1.25rem;
  }
  h3 {
    font-size: 1.3rem;
    font-weight: 600;
    line-height: 1.3;
    letter-spacing: -0.025em;
    margin: 0 0 0.9rem;
  }
  a:focus-visible,
  button:focus-visible,
  summary:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 5px;
  }
`
const Container = styled.div`
  width: min(100%, 1200px);
  margin: 0 auto;
  padding: 0 2.5rem;
  min-width: 0;
  @media (max-width: 600px) {
    padding: 0 1.25rem;
  }
`
const Hero = styled.header`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  color: #fff;
  background: #0b1b2a;
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    z-index: -1;
    pointer-events: none;
    background: linear-gradient(
      90deg,
      rgba(4, 14, 24, 0.78) 0%,
      rgba(4, 14, 24, 0.55) 43%,
      rgba(4, 14, 24, 0.05) 80%
    );
  }
  a:focus-visible {
    outline-color: #fff;
  }
  @media (max-width: 750px) {
    display: flex;
    flex-direction: column;
    &::after {
      display: none;
    }
  }
`
const HeroImage = styled(Img)`
  position: absolute !important;
  inset: 0;
  z-index: -2;
  width: 100%;
  height: 100%;
  @media (max-width: 750px) {
    position: relative !important;
    inset: auto;
    z-index: auto;
    order: 2;
    height: clamp(250px, 48vw, 340px);
  }
`
const HeroContent = styled(Container)`
  min-height: 610px;
  padding-top: 4.5rem;
  padding-bottom: 4.5rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  h1 {
    max-width: 670px;
    color: #fff;
    font-size: clamp(2.75rem, 5.1vw, 4.5rem);
    font-weight: 500;
    line-height: 1.07;
    letter-spacing: -0.045em;
    margin: 0 0 1.75rem;
    text-wrap: balance;
  }
  > p {
    max-width: 500px;
    color: #e0e6ec;
    font-size: 1.1rem;
    line-height: 1.75;
    margin: 0 0 2rem;
  }
  @media (max-width: 750px) {
    order: 1;
    min-height: auto;
    padding-top: 3rem;
    padding-bottom: 3rem;
    h1 {
      font-size: clamp(2.5rem, 8.8vw, 3.75rem);
    }
    > p {
      font-size: 1rem;
    }
  }
`
const Kicker = styled.p`
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  margin: 0 0 1.25rem;
  color: ${({ theme }) => theme.colors.primary};
`
const HeroKicker = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  margin-bottom: 1.75rem;
  &::before {
    content: "";
    flex-shrink: 0;
    width: 2rem;
    height: 2px;
    background: #6d9dff;
  }
`
const Actions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 1.25rem 2rem;
`
const PrimaryLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 1.2rem;
  padding: 1rem 1.25rem;
  border: 1px solid #fff;
  background: #fff;
  color: #102030 !important;
  font-size: 0.95rem;
  font-weight: 600;
  text-decoration: none;
  &:hover {
    background: #dce7ff;
    border-color: #dce7ff;
  }
  svg {
    flex-shrink: 0;
  }
`
const SecondaryLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  color: #fff !important;
  font-size: 0.95rem;
  text-decoration: none;
  padding: 0.75rem 0;
  border-bottom: 1px solid #8896a5;
  &:hover {
    border-color: #fff;
  }
`
const SectionNav = styled.nav`
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightBorder};
  background: ${({ theme }) => theme.colors.ednBackground};
  ${Container} {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  a {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    color: ${({ theme }) => theme.colors.text};
    font-size: 0.95rem;
    padding: 1.4rem 1.5rem;
    border-left: 1px solid ${({ theme }) => theme.colors.lightBorder};
    text-decoration: none;
  }
  a:last-child {
    border-right: 1px solid ${({ theme }) => theme.colors.lightBorder};
  }
  a:hover {
    color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.background};
  }
  svg {
    flex-shrink: 0;
  }
  @media (max-width: 600px) {
    ${Container} {
      grid-template-columns: 1fr;
    }
    a {
      padding: 1rem 0;
      border-left: 0;
      border-bottom: 1px solid ${({ theme }) => theme.colors.lightBorder};
    }
    a:last-child {
      border-right: 0;
      border-bottom: 0;
    }
  }
`
const Section = styled.section`
  padding: 5rem 0;
  scroll-margin-top: 6rem;
  @media (max-width: 600px) {
    padding: 3rem 0;
  }
`
const SectionIntro = styled.div`
  max-width: 770px;
  margin-bottom: 2.75rem;
  > p:last-child {
    max-width: 680px;
    font-size: 1.1rem;
    line-height: 1.75;
    color: ${({ theme }) => theme.colors.text200};
    margin: 0;
  }
`
const Method = styled(Section)`
  background: ${({ theme }) => theme.colors.ednBackground};
  border-block: 1px solid ${({ theme }) => theme.colors.lightBorder};
`
const Steps = styled.ol`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 2rem;
  list-style: none;
  margin: 0;
  padding: 0;
  li {
    margin: 0;
    padding: 1.5rem 0 0;
    border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
  }
  li::before {
    display: none;
  }
  span {
    display: block;
    color: ${({ theme }) => theme.colors.primary};
    font-size: 0.8rem;
    letter-spacing: 0.08em;
    margin-bottom: 1.75rem;
  }
  h3 {
    font-size: 1.15rem;
  }
  p {
    margin: 0;
    color: ${({ theme }) => theme.colors.text200};
    font-size: 0.95rem;
    line-height: 1.75;
  }
  @media (max-width: 950px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 550px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    span {
      margin-bottom: 0.75rem;
    }
  }
`
const Contact = styled(Section)`
  color: #fff;
  background: #0b1b2a;
  ${Container} {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    gap: 3rem;
  }
  ${Kicker} {
    color: #b4cae7;
  }
  h2 {
    color: #fff;
    max-width: 640px;
  }
  p:last-child {
    color: #d9e2eb;
    max-width: 670px;
    line-height: 1.75;
    font-size: 1.05rem;
    margin: 0;
  }
  a:focus-visible {
    outline-color: #fff;
  }
  @media (max-width: 950px) {
    ${Container} {
      grid-template-columns: 1fr;
      gap: 2rem;
    }
    a {
      justify-self: start;
    }
  }
`

const FAQ = styled.div`
  display: grid;
  grid-template-columns: 0.85fr 1.4fr;
  gap: 4rem;
  padding-top: 4rem;
  margin-top: 4rem;
  border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
  h2 {
    font-size: clamp(1.75rem, 2.5vw, 2.25rem);
  }
  details {
    border-bottom: 1px solid ${({ theme }) => theme.colors.lightBorder};
  }
  summary {
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1.5rem;
    padding: 1.35rem 0;
    font-size: 1.05rem;
    line-height: 1.4;
    list-style: none;
  }
  summary::-webkit-details-marker {
    display: none;
  }
  summary::after {
    content: "+";
    font-size: 1.4rem;
    color: ${({ theme }) => theme.colors.primary};
    flex-shrink: 0;
  }
  details[open] summary::after {
    content: "−";
  }
  summary:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
  details p {
    color: ${({ theme }) => theme.colors.text200};
    font-size: 0.95rem;
    line-height: 1.8;
    margin: 0;
    padding: 0 2rem 1.5rem 0;
  }
  @media (max-width: 800px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    padding-top: 3rem;
    margin-top: 3rem;
  }
`
const Related = styled.aside`
  margin-top: 4rem;
  padding-top: 2.5rem;
  border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
  h2 {
    font-size: 1.1rem;
    letter-spacing: 0;
  }
  > div {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem 2.5rem;
  }
  a {
    display: inline-flex;
    align-items: center;
    gap: 1rem;
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: none;
    font-size: 0.95rem;
    padding: 0.4rem 0;
  }
  a:hover {
    text-decoration: underline;
  }
`
const Programs = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-items: start;
  gap: 1.25rem;
  @media (max-width: 1000px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 650px) {
    grid-template-columns: 1fr;
  }
`
const Program = styled.article`
  padding: 2rem;
  border: 1px solid ${({ theme }) => theme.colors.lightBorder};
  background: ${({ theme }) => theme.colors.background};
  > div:first-child {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1.5rem;
  }
  > div:first-child svg {
    width: 1.75rem;
    height: 1.75rem;
    color: ${({ theme }) => theme.colors.primary};
  }
  > div:first-child span {
    font-size: 0.75rem;
    color: ${({ theme }) => theme.colors.text200};
  }
  h3 {
    min-height: 3.4rem;
  }
  > p {
    min-height: 6.5rem;
  }
  p {
    font-size: 0.95rem;
    line-height: 1.75;
    color: ${({ theme }) => theme.colors.text200};
    margin: 0;
  }
  details {
    margin-top: 1.75rem;
    border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
  }
  summary {
    list-style: none;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    cursor: pointer;
    padding-top: 1.2rem;
    font-size: 0.9rem;
    font-weight: 500;
    color: ${({ theme }) => theme.colors.primary};
  }
  summary::-webkit-details-marker {
    display: none;
  }
  summary::after {
    content: "+";
    font-size: 1.3rem;
    flex-shrink: 0;
  }
  details[open] summary::after {
    content: "−";
  }
  summary:hover span {
    text-decoration: underline;
  }
  ul {
    list-style: none;
    margin: 1.5rem 0;
    padding: 0;
  }
  li {
    display: flex;
    align-items: flex-start;
    gap: 0.65rem;
    padding: 0;
    margin: 0 0 0.8rem;
    font-size: 0.9rem;
    line-height: 1.6;
    color: ${({ theme }) => theme.colors.text200};
  }
  li::before {
    display: none;
  }
  li svg {
    color: ${({ theme }) => theme.colors.primary};
    flex-shrink: 0;
    margin-top: 0.25rem;
  }
  h4 {
    font-size: 0.7rem;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.primary};
    margin: 1.5rem 0 0.6rem;
    font-weight: 600;
  }
  @media (max-width: 650px) {
    h3,
    > p {
      min-height: 0;
    }
  }
  @media (max-width: 400px) {
    padding: 1.5rem;
  }
`
const Practice = styled(Section)`
  background: #0b1b2a;
  color: #fff;
  ${Container} {
    display: grid;
    grid-template-columns: 1fr 1.2fr;
    gap: 5rem;
    align-items: start;
  }
  ${Kicker} {
    color: #a9c6ed;
  }
  h2,
  h3 {
    color: #fff;
  }
  ${SectionIntro} {
    margin: 0;
  }
  ${SectionIntro} > p:last-child {
    color: #c2cedb;
  }
  @media (max-width: 850px) {
    ${Container} {
      grid-template-columns: 1fr;
      gap: 2.5rem;
    }
  }
`
const PracticeSteps = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  li {
    display: grid;
    grid-template-columns: 2rem 1fr;
    gap: 1.5rem;
    border-top: 1px solid #3a4c60;
    padding: 1.7rem 0;
    margin: 0;
  }
  li::before {
    display: none;
  }
  li:first-child {
    padding-top: 0;
    border-top: 0;
  }
  li:last-child {
    padding-bottom: 0;
  }
  span {
    color: #a9c6ed;
    font-size: 0.8rem;
    padding-top: 0.2rem;
  }
  h3 {
    font-size: 1.3rem;
    margin-bottom: 0.65rem;
  }
  p {
    color: #c2cedb;
    font-size: 1rem;
    line-height: 1.75;
    margin: 0;
  }
  @media (max-width: 400px) {
    li {
      grid-template-columns: 1.5rem 1fr;
      gap: 1rem;
    }
  }
`
const Materials = styled.div`
  margin-top: 3.5rem;
  padding-top: 2.5rem;
  border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
  > h3 {
    font-size: 1.15rem;
    margin-bottom: 1.75rem;
  }
  > div {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 2rem;
  }
  article {
    display: grid;
    grid-template-columns: 1.5rem 1fr;
    gap: 1rem;
  }
  svg {
    width: 1.25rem;
    height: 1.25rem;
    color: ${({ theme }) => theme.colors.primary};
    margin-top: 0.15rem;
  }
  h4 {
    font-size: 1rem;
    font-weight: 600;
    margin: 0 0 0.65rem;
  }
  p {
    font-size: 0.9rem;
    line-height: 1.7;
    color: ${({ theme }) => theme.colors.text200};
    margin: 0;
  }
  @media (max-width: 750px) {
    > div {
      grid-template-columns: 1fr;
      gap: 1.5rem;
    }
  }
`
const FAQSection = styled(Section)`
  ${FAQ} {
    border-top: 0;
    margin-top: 0;
    padding-top: 0;
  }
`
const programs = [
  { id: "software", icon: FiCode },
  { id: "cloud", icon: FiCloud },
  { id: "devsecops", icon: FiShield },
  { id: "ai", icon: FiCpu },
  { id: "architecture", icon: FiLayers },
  { id: "blockchain", icon: FiLink },
]
const practiceSteps = ["understand", "practice", "apply"]
const steps = ["assess", "design", "facilitate", "review"]
const materials = [
  { id: "resources", icon: FiBookOpen },
  { id: "exercises", icon: FiFolder },
  { id: "next", icon: FiTarget },
]
const related = [
  { id: "consulting", to: "/consultoria/" },
  { id: "development", to: "/desarrollo/" },
  { id: "ai", to: "/ai-service/" },
]

const TrainingPage = ({ data }) => {
  const intl = useIntl()
  const t = (id) => translateMessageId(`training-${id}`, intl)

  return (
    <Page>
      <PageMetadata
        title={t("meta-title")}
        description={t("meta-description")}
        image={data.hero.childImageSharp.fixed.src}
      />
      <Hero>
        <HeroImage
          fluid={data.hero.childImageSharp.fluid}
          alt={t("hero-alt")}
          loading="eager"
          imgStyle={{ objectPosition: "74% center" }}
        />
        <HeroContent>
          <HeroKicker>{t("eyebrow")}</HeroKicker>
          <h1>{t("title")}</h1>
          <p>{t("intro")}</p>
          <Actions>
            <PrimaryLink to="/#contacto">
              {t("contact")} <FiArrowRight aria-hidden="true" />
            </PrimaryLink>
            <SecondaryLink to="#programas">
              {t("explore")} <FiArrowDown aria-hidden="true" />
            </SecondaryLink>
          </Actions>
        </HeroContent>
      </Hero>
      <SectionNav aria-label={t("navigation")}>
        <Container>
          <Link to="#programas">
            {t("nav-programs")} <FiArrowDown aria-hidden="true" />
          </Link>
          <Link to="#aprendizaje">
            {t("nav-practice")} <FiArrowDown aria-hidden="true" />
          </Link>
          <Link to="#ruta">
            {t("nav-route")} <FiArrowDown aria-hidden="true" />
          </Link>
        </Container>
      </SectionNav>
      <Section id="programas" aria-labelledby="training-programs-title">
        <Container>
          <SectionIntro>
            <Kicker>{t("programs-eyebrow")}</Kicker>
            <h2 id="training-programs-title">{t("programs-title")}</h2>
            <p>{t("programs-intro")}</p>
          </SectionIntro>
          <Programs>
            {programs.map(({ id, icon: Icon }, index) => (
              <Program key={id}>
                <div>
                  <Icon aria-hidden="true" />
                  <span aria-hidden="true">0{index + 1}</span>
                </div>
                <h3>{t(`program-${id}-title`)}</h3>
                <p>{t(`program-${id}-description`)}</p>
                <details>
                  <summary
                    aria-label={`${t("program-open")}: ${t(
                      `program-${id}-title`
                    )}`}
                  >
                    <span>{t("program-open")}</span>
                  </summary>
                  <ul>
                    {[1, 2, 3].map((item) => (
                      <li key={item}>
                        <FiCheck aria-hidden="true" />
                        {t(`program-${id}-topic-${item}`)}
                      </li>
                    ))}
                  </ul>
                  <h4>{t("program-practice")}</h4>
                  <p>{t(`program-${id}-practice`)}</p>
                </details>
              </Program>
            ))}
          </Programs>
        </Container>
      </Section>
      <Practice id="aprendizaje" aria-labelledby="training-practice-title">
        <Container>
          <SectionIntro>
            <Kicker>{t("practice-eyebrow")}</Kicker>
            <h2 id="training-practice-title">{t("practice-title")}</h2>
            <p>{t("practice-intro")}</p>
          </SectionIntro>
          <PracticeSteps>
            {practiceSteps.map((id, index) => (
              <li key={id}>
                <span aria-hidden="true">0{index + 1}</span>
                <div>
                  <h3>{t(`practice-${id}-title`)}</h3>
                  <p>{t(`practice-${id}-description`)}</p>
                </div>
              </li>
            ))}
          </PracticeSteps>
        </Container>
      </Practice>
      <Method id="ruta" aria-labelledby="training-route-title">
        <Container>
          <SectionIntro>
            <Kicker>{t("route-eyebrow")}</Kicker>
            <h2 id="training-route-title">{t("route-title")}</h2>
            <p>{t("route-intro")}</p>
          </SectionIntro>
          <Steps>
            {steps.map((id, index) => (
              <li key={id}>
                <span aria-hidden="true">0{index + 1}</span>
                <h3>{t(`route-${id}-title`)}</h3>
                <p>{t(`route-${id}-description`)}</p>
              </li>
            ))}
          </Steps>
          <Materials>
            <h3>{t("materials-title")}</h3>
            <div>
              {materials.map(({ id, icon: Icon }) => (
                <article key={id}>
                  <Icon aria-hidden="true" />
                  <div>
                    <h4>{t(`materials-${id}-title`)}</h4>
                    <p>{t(`materials-${id}-description`)}</p>
                  </div>
                </article>
              ))}
            </div>
          </Materials>
        </Container>
      </Method>
      <FAQSection aria-labelledby="training-faq-title">
        <Container>
          <FAQ>
            <div>
              <Kicker>{t("faq-eyebrow")}</Kicker>
              <h2 id="training-faq-title">{t("faq-title")}</h2>
            </div>
            <div>
              {["level", "project", "format"].map((id) => (
                <details key={id}>
                  <summary>{t(`faq-${id}-question`)}</summary>
                  <p>{t(`faq-${id}-answer`)}</p>
                </details>
              ))}
            </div>
          </FAQ>
          <Related aria-labelledby="training-related-title">
            <h2 id="training-related-title">{t("related-title")}</h2>
            <div>
              {related.map(({ id, to }) => (
                <Link key={id} to={to}>
                  {t(`related-${id}`)} <FiArrowRight aria-hidden="true" />
                </Link>
              ))}
            </div>
          </Related>
        </Container>
      </FAQSection>
      <Contact aria-labelledby="training-contact-title">
        <Container>
          <div>
            <Kicker>{t("cta-eyebrow")}</Kicker>
            <h2 id="training-contact-title">{t("cta-title")}</h2>
            <p>{t("cta-description")}</p>
          </div>
          <PrimaryLink to="/#contacto">
            {t("contact")} <FiArrowRight aria-hidden="true" />
          </PrimaryLink>
        </Container>
      </Contact>
    </Page>
  )
}

export default TrainingPage

export const query = graphql`
  query TrainingPageQuery {
    hero: file(relativePath: { eq: "capacitacion/team-learning-v1.png" }) {
      childImageSharp {
        fluid(maxWidth: 1920, quality: 85) {
          ...GatsbyImageSharpFluid_withWebp
        }
        fixed(width: 1200) {
          src
        }
      }
    }
  }
`
